import type { MaterialOrigin } from '../../../types/factory';

// Plano de cada planta: la imagen y los sectores que se pueden tocar. Las cajas van en píxeles de la
// imagen (x, y, ancho, alto) y cada sector dice con qué datos se engancha: una máquina por nombre, un
// grupo de productos, materias primas por origen, ubicaciones del depósito por sector, etc.

export type ZoneKind =
  | 'machine' | 'molds' | 'products' | 'materials' | 'mix' | 'grinding' | 'scale'
  | 'wip' | 'inputs' | 'delivery' | 'office' | 'soon';

export interface PlantZone {
  id: string;
  label: string;
  kind: ZoneKind;
  box: [number, number, number, number];
  /** machine: nombre de la máquina (no matriz). */
  machine?: RegExp;
  /** products / wip: nombres de producto. */
  products?: RegExp;
  /** materials / inputs: origen de la MP o nombre. */
  origins?: MaterialOrigin[];
  materials?: RegExp;
  /** Ubicaciones del depósito que caen en este sector (por sector, código o nombre). */
  sector?: RegExp;
  /** Sectores que todavía no tienen datos: qué van a mostrar. */
  soon?: string[];
  /** Dónde se carga lo de este sector. */
  link?: { label: string; path: string };
}

export interface PlantLayout {
  name: string;
  image: string;
  width: number;
  height: number;
  zones: PlantZone[];
}

const injector = (n: number, box: PlantZone['box']): PlantZone => ({
  id: `iny-${n}`, label: `Inyectora ${n}`, kind: 'machine', box,
  machine: new RegExp(`(iny\\w*|^i)\\s*-?0?${n}\\b`, 'i'),
});

/** Parriplast (Asener S.A.): planta ajustada al relevamiento de octubre 2026. */
const ASENER: PlantLayout = {
  name: 'Parriplast',
  image: '/tenant-plants/asener.webp',
  width: 1024,
  height: 1400,
  zones: [
    injector(1, [95, 110, 205, 108]),
    injector(2, [95, 265, 205, 112]),
    injector(3, [95, 420, 205, 112]),
    injector(4, [90, 578, 210, 114]),
    injector(5, [90, 735, 210, 104]),
    injector(6, [85, 895, 212, 108]),
    {
      id: 'sopladora', label: 'Sopladora de botellas', kind: 'machine', box: [665, 283, 225, 147],
      machine: /soplad/i,
    },
    {
      id: 'matrices', label: 'Matrices', kind: 'molds', box: [305, 55, 100, 90],
      link: { label: 'Mantenimiento', path: '/factory/maintenance' },
    },
    {
      id: 'triturado', label: 'Triturado y mezclado', kind: 'grinding', box: [440, 50, 295, 207],
      link: { label: 'Materia prima', path: '/factory/materials' },
    },
    {
      id: 'stock-botellas', label: 'Stock de botellas', kind: 'products', box: [455, 318, 100, 177],
      products: /botell/i, sector: /botell/i,
    },
    {
      id: 'stock-baldes', label: 'Stock de baldes y fratachos', kind: 'products', box: [455, 497, 100, 328],
      products: /balde|fratach|batea|mango/i, sector: /balde|fratach|batea/i,
    },
    {
      id: 'stock-tapones', label: 'Stock de tapones', kind: 'products', box: [455, 828, 100, 222],
      products: /tap[oó]n/i, sector: /tap[oó]n/i,
    },
    {
      id: 'balanza', label: 'Balanza electrónica', kind: 'scale', box: [455, 1063, 100, 88],
    },
    {
      id: 'en-fabricacion', label: 'Productos en fabricación', kind: 'wip', box: [350, 330, 80, 605],
      sector: /fabricaci|proceso/i,
      link: { label: 'Producción', path: '/factory/production' },
    },
    {
      id: 'insumos', label: 'Insumos: O-rings y alambres', kind: 'inputs', box: [350, 940, 80, 210],
      materials: /o-?ring|alambre|manija|asa\b/i, sector: /o-?ring|alambre/i,
    },
    {
      id: 'recuperado', label: 'Recuperado', kind: 'materials', box: [668, 450, 227, 140],
      origins: ['RecuperadaPropia', 'RecuperadaComprada'],
    },
    {
      id: 'mezclado', label: 'Mezclado', kind: 'mix', box: [668, 592, 227, 143],
    },
    {
      id: 'mp-virgen', label: 'Materia prima virgen', kind: 'materials', box: [670, 738, 245, 172],
      origins: ['Virgen'],
    },
    {
      id: 'camioneta', label: 'Entregas', kind: 'delivery', box: [545, 1200, 95, 180],
      link: { label: 'OC de clientes', path: '/factory/customer-orders' },
    },
    {
      id: 'oficina', label: 'Oficina', kind: 'office', box: [45, 1175, 232, 108],
    },
    {
      id: 'panol-herramientas', label: 'Pañol de herramientas', kind: 'soon', box: [320, 148, 105, 102],
      soon: [
        'Herramientas disponibles sobre el total y quién tiene cada una.',
        'Retiro y devolución escaneando el QR: queda quién la llevó y para qué máquina.',
        'Préstamos sin devolver, herramientas dañadas y calibraciones próximas.',
      ],
    },
    {
      id: 'pintura', label: 'Pintura y pegado', kind: 'soon', box: [745, 70, 145, 167],
      soon: ['Tapones y botellas esperando pintura QR.', 'Pegado y embalado de fratachos por turno.'],
      link: { label: 'Armado afuera', path: '/factory/outwork' },
    },
    {
      id: 'armado-tapones', label: 'Armado de tapones', kind: 'soon', box: [195, 1028, 110, 118],
      soon: ['Tapones sin oring esperando colocación.', 'Bolsas armadas por turno y por operario.'],
      link: { label: 'Armado afuera', path: '/factory/outwork' },
    },
    {
      id: 'maquina-qr', label: 'Máquina nueva y QR', kind: 'soon', box: [60, 1013, 135, 97],
      soon: ['Estado y producción de la máquina de QR cuando esté instalada.'],
    },
    {
      id: 'panol-maquinarias', label: 'Pañol de maquinarias', kind: 'soon', box: [705, 1105, 285, 125],
      soon: ['Repuestos para mantenimiento con su stock mínimo.', 'Qué repuesto se usó en cada intervención.'],
      link: { label: 'Mantenimiento', path: '/factory/maintenance' },
    },
  ],
};

const LAYOUTS: { match: RegExp; layout: PlantLayout }[] = [
  { match: /asener|parriplast/i, layout: ASENER },
];

/** Plano del tenant logueado (por subdominio o nombre); null si su planta todavía no tiene plano. */
export function plantLayoutForTenant(tenant?: { subdomain?: string | null; name?: string | null } | null): PlantLayout | null {
  try {
    const t = tenant ?? JSON.parse(localStorage.getItem('auth_tenant') || 'null');
    const key = `${t?.subdomain || ''} ${t?.name || ''}`;
    return LAYOUTS.find(l => l.match.test(key))?.layout ?? null;
  } catch {
    return null;
  }
}

/**
 * Tenants con sistema de planta propio (barra, menú e inicio aparte del DashFlow de siempre).
 * Hoy es solo Parriplast (Asener); el resto de los tenants no ve ningún cambio.
 */
export const hasPlantShell = (tenant?: { subdomain?: string | null; name?: string | null } | null) =>
  !!tenant && plantLayoutForTenant(tenant) !== null;
